/**
 * AppPanelHeader Component
 * 
 * The header bar for the AppPanel that displays:
 * - App name with icon + Database link
 * - View mode selector
 * - Contextual controls (refresh for Preview, class selector for Database, etc.)
 * - Public URL (when available)
 * - Publish button
 * 
 * Maintains consistent styling with the existing glass-morphism design system.
 */

import { useMemo } from 'react';
import { ViewMode, ParseClass, PublicFile } from '../../types/AppPanel';
import ViewModeSelector from './ViewModeSelector';
import PreviewUrlBar from './PreviewUrlBar';
import ClassSelectorBar from './ClassSelectorBar';

interface AppPanelHeaderProps {
  /** Name of the Parse app */
  appName?: string;
  /** Current view mode */
  viewMode: ViewMode;
  /** Callback when view mode changes */
  onViewModeChange: (mode: ViewMode) => void;
  /** Public URL of the app (if published) */
  publicUrl?: string;
  /** URL to the backend dashboard */
  backendHref?: string;
  /** Whether the app has been created (enables view mode selector) */
  hasApp: boolean;
  /** Label for the publish button */
  publishButtonLabel: string;
  /** Whether publish button is disabled */
  publishDisabled: boolean;
  /** Callback for publish button click */
  onPublishClick: () => void;
  
  // Preview context props
  /** List of public HTML pages */
  publicFiles?: PublicFile[];
  /** Currently selected page */
  currentPage?: string;
  /** Callback when page changes */
  onPageChange?: (path: string) => void;
  /** Callback to refresh preview */
  onRefresh?: () => void;
  /** Preview URL for open in new tab */
  previewUrl?: string;
  
  // Database context props
  /** List of database classes */
  parseClasses?: ParseClass[];
  /** Currently selected class */
  selectedClass?: string | null;
  /** Callback when class changes */
  onClassChange?: (className: string) => void;
  
  // Cloud Code context props
  /** Current cloud code sub-view */
  cloudCodeSubView?: 'files' | 'functions';
  /** Callback when cloud code sub-view changes */
  onCloudCodeSubViewChange?: (subView: 'files' | 'functions') => void;
  
  // Refresh callbacks for each section
  /** Callback to refresh database data */
  onRefreshDatabase?: () => void;
  /** Callback to refresh cloud code data */
  onRefreshCloudCode?: () => void;
  /** Callback to refresh deployments data */
  onRefreshDeployments?: () => void;
  /** Whether any data is currently being refreshed */
  isRefreshing?: boolean;
}

/**
 * Normalizes a URL to ensure it has https:// prefix
 */
function normalizeUrl(input?: string): string | undefined {
  if (!input) return undefined;
  const trimmed = input.trim();
  if (!trimmed) return undefined;
  return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
}

export default function AppPanelHeader({
  appName,
  viewMode,
  onViewModeChange,
  publicUrl,
  backendHref,
  hasApp,
  publishButtonLabel,
  publishDisabled,
  onPublishClick,
  // Preview context
  publicFiles,
  currentPage,
  onPageChange,
  onRefresh,
  previewUrl,
  // Database context
  parseClasses,
  selectedClass,
  onClassChange,
  // Cloud Code context
  cloudCodeSubView,
  onCloudCodeSubViewChange,
  // Refresh callbacks
  onRefreshDatabase,
  onRefreshCloudCode,
  onRefreshDeployments,
  isRefreshing,
}: AppPanelHeaderProps) {
  // Normalize URLs for display and linking
  const displayUrl = useMemo(() => normalizeUrl(publicUrl), [publicUrl]);
  
  // Build database browser URL (default link destination)
  const databaseBrowserHref = useMemo(() => {
    if (!backendHref) return undefined;
    const base = /^https?:\/\//i.test(backendHref) ? backendHref : `https://${backendHref}`;
    // Replace /overview with /browser, or append /browser
    return base.replace(/\/?overview\/?$/i, '/browser').replace(/\/?$/, '/browser').replace(/\/browser\/browser/, '/browser');
  }, [backendHref]);

  // Render contextual controls based on view mode
  const renderContextualControls = () => {
    switch (viewMode) {
      case 'preview':
        return (
          <PreviewUrlBar
            baseUrl={previewUrl}
            publicFiles={publicFiles}
            currentPath={currentPage || '/'}
            onPathChange={onPageChange}
            onRefresh={onRefresh}
            onOpenExternal={previewUrl ? () => {
              const raw = previewUrl.trim();
              const origin = /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
              const base = origin.replace(/\/+$/, '');
              const path = (currentPage || '/').trim();
              const normalizedPath = path.startsWith('/') ? path : `/${path}`;
              window.open(`${base}${normalizedPath}`, '_blank', 'noopener,noreferrer');
            } : undefined}
          />
        );

      case 'database':
        return (
          <div className="flex items-center gap-2">
            <ClassSelectorBar
              classes={parseClasses}
              selectedClass={selectedClass}
              onClassChange={onClassChange}
              dashboardUrl={backendHref}
            />
            {/* Refresh Database Button */}
            {onRefreshDatabase && (
              <button
                onClick={onRefreshDatabase}
                disabled={isRefreshing}
                className={`
                  p-2 rounded-lg transition-all
                  ${isRefreshing 
                    ? 'text-white/30 cursor-not-allowed' 
                    : 'text-white/50 hover:text-white hover:bg-white/10'}
                `}
                title="Refresh database"
              >
                <svg 
                  width="16" 
                  height="16" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  stroke="currentColor" 
                  strokeWidth="2"
                  className={isRefreshing ? 'animate-spin' : ''}
                >
                  <path d="M23 4v6h-6M1 20v-6h6"/>
                  <path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15"/>
                </svg>
              </button>
            )}
          </div>
        );

      case 'cloud-code':
        return (
          <div className="flex items-center gap-2 sm:gap-4">
            {/* Files/Functions Toggle - responsive: icons only on small screens */}
            <div className="flex items-center bg-white/5 rounded-lg p-0.5 border border-white/10">
              <button
                onClick={() => onCloudCodeSubViewChange?.('files')}
                className={`
                  flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-1.5 rounded-md text-sm font-medium
                  transition-all duration-200
                  ${cloudCodeSubView === 'files' 
                    ? 'bg-white/10 text-white' 
                    : 'text-white/50 hover:text-white/70'}
                `}
                title="Files"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2v11z" />
                </svg>
                <span className="hidden md:inline">Files</span>
              </button>
              <button
                onClick={() => onCloudCodeSubViewChange?.('functions')}
                className={`
                  flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-1.5 rounded-md text-sm font-medium
                  transition-all duration-200
                  ${cloudCodeSubView === 'functions' 
                    ? 'bg-white/10 text-white' 
                    : 'text-white/50 hover:text-white/70'}
                `}
                title="Functions"
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M13 2L3 14H12L11 22L21 10H12L13 2Z" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span className="hidden md:inline">Functions</span>
              </button>
            </div>

            {/* Refresh Cloud Code Button */}
            {onRefreshCloudCode && (
              <button
                onClick={onRefreshCloudCode}
                disabled={isRefreshing}
                className={`
                  p-2 rounded-lg transition-all
                  ${isRefreshing 
                    ? 'text-white/30 cursor-not-allowed' 
                    : 'text-white/50 hover:text-white hover:bg-white/10'}
                `}
                title="Refresh cloud code"
              >
                <svg 
                  width="16" 
                  height="16" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  stroke="currentColor" 
                  strokeWidth="2"
                  className={isRefreshing ? 'animate-spin' : ''}
                >
                  <path d="M23 4v6h-6M1 20v-6h6"/>
                  <path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15"/>
                </svg>
              </button>
            )}
          </div>
        );

      case 'deployments':
        return (
          <div className="flex items-center gap-2 sm:gap-3">
            {/* Label hidden on small screens */}
            <span className="hidden md:inline text-sm text-white/50">Deployment History</span>
            {/* Refresh Deployments Button */}
            {onRefreshDeployments && (
              <button
                onClick={onRefreshDeployments}
                disabled={isRefreshing}
                className={`
                  p-2 rounded-lg transition-all
                  ${isRefreshing 
                    ? 'text-white/30 cursor-not-allowed' 
                    : 'text-white/50 hover:text-white hover:bg-white/10'}
                `}
                title="Refresh deployments"
              >
                <svg 
                  width="16" 
                  height="16" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  stroke="currentColor" 
                  strokeWidth="2"
                  className={isRefreshing ? 'animate-spin' : ''}
                >
                  <path d="M23 4v6h-6M1 20v-6h6"/>
                  <path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15"/>
                </svg>
              </button>
            )}
          </div>
        );

      case 'logs':
        return (
          <span className="text-sm text-white/50">System Logs</span>
        );

      default:
        return null;
    }
  };

  return (
    <header className="relative z-[100] flex items-center justify-between gap-4 p-2 lg:px-4 lg:py-3 glass-header rounded-t-xl border-b border-white/5">
      {/* Left Section: App Name + Database Icon */}
      <div className="flex items-center gap-2 sm:gap-3 min-w-[80px] flex-shrink">
        {/* App Icon - hidden on mobile */}
        <div className="hidden lg:flex flex-shrink-0 w-8 h-8 rounded-lg bg-gradient-to-br from-[#208AEC] to-[#1a6dba] items-center justify-center shadow-lg shadow-[#208AEC]/20">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
            <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M2 17L12 22L22 17" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            <path d="M2 12L12 17L22 12" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
        
        {/* App Name - responsive truncation */}
        <span className="text-white font-semibold text-base truncate max-w-[80px] sm:max-w-[100px] md:max-w-[140px] lg:max-w-[180px] xl:max-w-[220px]">
          {appName || 'Untitled App'}
        </span>

        {/* Database Link (icon only) - links to /browser, hidden on mobile */}
        {databaseBrowserHref && (
          <a
            href={databaseBrowserHref}
            target="_blank"
            rel="noreferrer noopener"
            className="hidden lg:flex flex-shrink-0 p-1.5 rounded-md hover:bg-white/10 text-white/50 hover:text-white/80 transition-colors"
            title="Open Database Browser"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path d="M18 13V19C18 20.1046 17.1046 21 16 21H5C3.89543 21 3 20.1046 3 19V8C3 6.89543 3.89543 6 5 6H11" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M15 3H21V9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M10 14L21 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </a>
        )}
      </div>

      {/* Mobile: Show only PreviewUrlBar when in preview mode */}
      {viewMode === 'preview' && (
        <div className="flex lg:hidden items-center flex-1 min-w-0">
          <PreviewUrlBar
            baseUrl={previewUrl}
            publicFiles={publicFiles}
            currentPath={currentPage || '/'}
            onPathChange={onPageChange}
            onRefresh={onRefresh}
            onOpenExternal={previewUrl ? () => {
              const raw = previewUrl.trim();
              const origin = /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
              const base = origin.replace(/\/+$/, '');
              const path = (currentPage || '/').trim();
              const normalizedPath = path.startsWith('/') ? path : `/${path}`;
              window.open(`${base}${normalizedPath}`, '_blank', 'noopener,noreferrer');
            } : undefined}
          />
        </div>
      )}

      {/* Separator - hidden on mobile */}
      <div className="hidden lg:block w-px h-6 bg-white/10 flex-shrink-0" />

      {/* Center Section: View Mode Selector + Contextual Controls - hidden on mobile */}
      <div className="hidden lg:flex items-center gap-2 sm:gap-4 flex-1 min-w-0">
        {/* View Mode Selector */}
        <ViewModeSelector
          value={viewMode}
          onChange={onViewModeChange}
          disabled={!hasApp}
        />

        {/* Contextual Controls */}
        {hasApp && renderContextualControls()}
      </div>

      {/* Right Section: URL (if exists) + Publish Button */}
      <div className="flex items-center gap-2 sm:gap-4 flex-shrink-0">
        {/* Public URL - only shows if URL exists */}
        {displayUrl && (
          <a
            href={displayUrl}
            target="_blank"
            rel="noreferrer noopener"
            className="hidden md:flex items-center gap-2 text-[#208AEC] hover:text-[#60a5fa] transition-colors duration-200"
            title={displayUrl}
          >
            {/* Icon */}
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="flex-shrink-0">
              <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6"/>
              <path d="M15 3h6v6"/>
              <path d="M10 14L21 3"/>
            </svg>
            {/* URL text on lg+ screens */}
            <span className="hidden lg:inline text-sm font-medium truncate max-w-[120px] xl:max-w-[180px]">
              {displayUrl.replace(/^https?:\/\//, '')}
            </span>
          </a>
        )}

        {/* Publish Button */}
        <button
          onClick={onPublishClick}
          disabled={publishDisabled}
          className={`
            text-sm px-3 sm:px-4 py-2 rounded-lg font-semibold
            transition-all duration-200
            ${publishDisabled
              ? 'bg-white/5 text-white/40 cursor-not-allowed border border-white/10'
              : 'bg-gradient-to-r from-[#22c55e] to-[#16a34a] text-white hover:from-[#16a34a] hover:to-[#15803d] shadow-lg shadow-[#22c55e]/20 hover:shadow-[#22c55e]/30 border border-[#22c55e]/30'
            }
          `}
        >
          {publishButtonLabel}
        </button>
      </div>
    </header>
  );
}
